import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import * as XLSX from "xlsx";

export default function Reports() {
  const { currentShopId } = useShop();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  const dropdownRef = useRef(null);

  const load = useCallback(async () => {
    if (!currentShopId) return;

    try {
      const params = {
        shop_id: currentShopId,
      };

      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await api.get("/reports", { params });

      setData(data);
    } catch (error) {
      console.error("Failed to load reports:", error);
    }
  }, [currentShopId, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const btn = openItem ? buttonRefs.current[openItem] : null;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        (!btn || !btn.contains(e.target))
      ) {
        setOpenItem(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openItem]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!openItem) return;
    const reposition = () => {
      const btn = buttonRefs.current[openItem];
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(8, rect.right + window.scrollX - 220),
      });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [openItem]);

  const toggleDropdown = (itemName) => {
    if (openItem === itemName) {
      setOpenItem(null);
      return;
    }
    const btn = buttonRefs.current[itemName];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(8, rect.right + window.scrollX - 220),
      });
    }
    setOpenItem(itemName);
  };

  // ==============================
  // EXPORT REPORT TO EXCEL
  // ==============================
  const exportExcel = () => {
    if (!data) return;

    try {
      // ------------------------------
      // Summary data
      // ------------------------------
      const summaryRows = [
        ["Kitchen Report"],
        [],
        ["Report From", dateFrom || "All dates"],
        ["Report To", dateTo || "All dates"],
        [],
        ["Summary"],
        ["Revenue", data.revenue],
        ["Completed Orders", data.completed_orders],
        ["Average Ticket", data.average_ticket],
        ["Online Mix (%)", data.online_mix_pct],
      ];

      // ------------------------------
      // Item-wise data
      // ------------------------------
      const itemRows = [
        [],
        ["Item-wise Report"],
        ["Item", "Qty", "Total"],
        ...data.item_wise.map((item) => [
          item.name,
          item.qty,
          item.total,
        ]),
      ];

      // ------------------------------
      // Category-wise data
      // ------------------------------
      const categoryRows = [
        [],
        ["Category-wise Report"],
        ["Category", "Qty", "Total"],
        ...data.category_wise.map((category) => [
          category.name,
          category.qty,
          category.total,
        ]),
      ];

      // ------------------------------
      // Customer + item data
      // ------------------------------
      const customerItemRows = [
        [],
        ["Customer & Item Report"],
        ["Customer", "Item", "Qty", "Price", "Total", "Order #", "Order Type"],
        ...(data.customer_items || []).map((ci) => [
          ci.customer_name,
          ci.item_name,
          ci.qty,
          ci.price,
          ci.total,
          ci.order_number,
          ci.order_type === "LiveCounter" ? "Live counter" : ci.order_type === "Takeaway" ? "Takeaway" : (ci.order_type || "—"),
        ]),
      ];

      // Combine all rows
      const rows = [
        ...summaryRows,
        ...itemRows,
        ...categoryRows,
        ...customerItemRows,
      ];

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(rows);

      // Set column widths
      worksheet["!cols"] = [
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
      ];

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Add worksheet
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Kitchen Report"
      );

      // Generate filename
      const today = new Date().toISOString().split("T")[0];

      const fileName = `Kitchen_Report_${today}.xlsx`;

      // Download Excel file
      XLSX.writeFile(workbook, fileName);

    } catch (error) {
      console.error("Excel export failed:", error);
      alert("Could not export Excel report");
    }
  };

  const buyersForItem = (itemName) =>
    (data?.customer_items || []).filter((ci) => ci.item_name === itemName);

  return (
    <section
      className="view"
      data-testid="reports-page"
    >
      {/* ==============================
          PAGE HEADER
      ============================== */}
      <div className="page-head">
        <div>
          <div className="eyebrow">
            Business pulse
          </div>

          <h1>
            Reports
          </h1>

          <p>
            A compact read on orders, revenue, and channel mix.
          </p>
        </div>

        {/* ==============================
            EXPORT EXCEL BUTTON
        ============================== */}
        <button
          className="btn btn-secondary"
          onClick={exportExcel}
          disabled={!data}
          data-testid="export-report-btn"
        >
          ⇩ Export Excel
        </button>
      </div>

      {/* ==============================
          DATE FILTER
      ============================== */}
      <div className="card">
        <div className="toolbar">

          <input
            type="date"
            className="input"
            style={{ minWidth: 150 }}
            value={dateFrom}
            onChange={(e) =>
              setDateFrom(e.target.value)
            }
            data-testid="report-date-from"
          />

          <input
            type="date"
            className="input"
            style={{ minWidth: 150 }}
            value={dateTo}
            onChange={(e) =>
              setDateTo(e.target.value)
            }
            data-testid="report-date-to"
          />

        </div>
      </div>

      {/* ==============================
          REPORT DATA
      ============================== */}
      {data && (
        <>
          {/* ==============================
              STATISTICS
          ============================== */}
          <div
            className="stats"
            style={{ marginTop: 16 }}
          >

            <div className="card stat">
              <div className="stat-icon">
                ₹
              </div>

              <div className="stat-value">
                ₹{data.revenue.toFixed(0)}
              </div>

              <div className="stat-label">
                Revenue
              </div>
            </div>

            <div className="card stat">
              <div className="stat-icon">
                ▤
              </div>

              <div className="stat-value">
                {data.completed_orders}
              </div>

              <div className="stat-label">
                Completed orders
              </div>
            </div>

            <div className="card stat">
              <div className="stat-icon">
                ◒
              </div>

              <div className="stat-value">
                ₹{data.average_ticket.toFixed(0)}
              </div>

              <div className="stat-label">
                Average ticket
              </div>
            </div>

            <div className="card stat">
              <div className="stat-icon">
                ◉
              </div>

              <div className="stat-value">
                {data.online_mix_pct.toFixed(0)}%
              </div>

              <div className="stat-label">
                Online mix
              </div>
            </div>

          </div>

          {/* ==============================
              TABLES
          ============================== */}
          <div className="dashboard-grid">

            {/* ==============================
                ITEM-WISE
            ============================== */}
            <div className="card">

              <div className="section-head">
                <div>
                  <h2>
                    Item-wise
                  </h2>
                </div>
              </div>

              <table>
                <tbody>

                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Total</th>
                    <th></th>
                  </tr>

                  {data.item_wise.map((item) => (
                    <tr key={item.name}>

                      <td>
                        {item.name}
                      </td>

                      <td className="mono">
                        {item.qty}
                      </td>

                      <td className="mono">
                        ₹{item.total.toFixed(0)}
                      </td>

                      <td>
                        <button
                          ref={(el) => (buttonRefs.current[item.name] = el)}
                          className="btn btn-secondary btn-sm"
                          onClick={() => toggleDropdown(item.name)}
                          data-testid={`view-buyers-${item.name}`}
                        >
                          Buyers ▾
                        </button>
                      </td>

                    </tr>
                  ))}

                </tbody>
              </table>

              {data.item_wise.length === 0 && (
                <div
                  className="empty"
                  style={{ margin: 16 }}
                >
                  No data yet
                </div>
              )}

            </div>

            {/* ==============================
                CATEGORY-WISE
            ============================== */}
            <div className="card">

              <div className="section-head">
                <div>
                  <h2>
                    Category-wise
                  </h2>
                </div>
              </div>

              <table>
                <tbody>

                  <tr>
                    <th>Category</th>
                    <th>Qty</th>
                    <th>Total</th>
                  </tr>

                  {data.category_wise.map((category) => (
                    <tr key={category.name}>

                      <td>
                        {category.name}
                      </td>

                      <td className="mono">
                        {category.qty}
                      </td>

                      <td className="mono">
                        ₹{category.total.toFixed(0)}
                      </td>

                    </tr>
                  ))}

                </tbody>
              </table>

              {data.category_wise.length === 0 && (
                <div
                  className="empty"
                  style={{ margin: 16 }}
                >
                  No data yet
                </div>
              )}

            </div>

          </div>

          {/* ==============================
              CUSTOMER & ITEM REPORT
          ============================== */}
          <div className="card" style={{ marginTop: 16 }}>

            <div className="section-head">
              <div>
                <h2>
                  Customer &amp; item
                </h2>
              </div>
            </div>

            <table>
              <tbody>

                <tr>
                  <th>Customer</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Total</th>
                  <th>Order type</th>
                </tr>

                {(data.customer_items || []).map((ci, idx) => (
                  <tr key={`${ci.order_number}-${ci.item_name}-${idx}`}>

                    <td>
                      {ci.customer_name}
                    </td>

                    <td>
                      {ci.item_name}
                    </td>

                    <td className="mono">
                      {ci.qty}
                    </td>

                    <td className="mono">
                      ₹{Number(ci.total).toFixed(0)}
                    </td>

                    <td>
                      {ci.order_type === "LiveCounter" ? "Live counter" : ci.order_type === "Takeaway" ? "Takeaway" : (ci.order_type || "—")}
                    </td>

                  </tr>
                ))}

              </tbody>
            </table>

            {(!data.customer_items || data.customer_items.length === 0) && (
              <div
                className="empty"
                style={{ margin: 16 }}
              >
                No data yet
              </div>
            )}

          </div>
        </>
      )}

      {/* ==============================
          BUYERS DROPDOWN (rendered via portal so it can never be clipped)
      ============================== */}
      {openItem &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              zIndex: 1000,
              background: "var(--surface, #fff)",
              border: "1px solid var(--border, #ddd)",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {buyersForItem(openItem).length === 0 && (
              <div style={{ padding: 12, fontSize: 13, color: "#888" }}>
                No buyers found
              </div>
            )}
            {buyersForItem(openItem).map((ci, idx) => (
              <div
                key={`${ci.order_number}-${idx}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--border, #eee)",
                  fontSize: 13,
                }}
              >
                <span>{ci.customer_name}{ci.qty > 1 ? ` (×${ci.qty})` : ""}</span>
                <span className="mono">₹{Number(ci.total).toFixed(0)}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </section>
  );
}